import { createRootRoute, Link, Outlet, useLocation } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useAuth } from '../lib/auth'

export const Route = createRootRoute({
  component: RootComponent,
})

interface NavItem {
  to: string
  label: string
  icon: string
  roles?: Array<'admin' | 'manager' | 'user'>
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Дашборд', icon: '📊' },
  { to: '/submit', label: 'Подать идею', icon: '＋' },
  { to: '/my-ideas', label: 'Мои идеи', icon: '📂' },
  { to: '/inbox', label: 'Входящие', icon: '📥', roles: ['admin', 'manager'] },
  { to: '/meta-ideas', label: 'Мета-идеи', icon: '🧩', roles: ['admin', 'manager'] },
  { to: '/batch-similarity', label: 'Проверка Excel', icon: '📋', roles: ['admin', 'manager'] },
  { to: '/search', label: 'Поиск', icon: '🔍' },
  { to: '/admin/users', label: 'Пользователи', icon: '👥', roles: ['admin'] },
]

function RootComponent() {
  const { user, loading, load, logout } = useAuth()
  const location = useLocation()

  useEffect(() => {
    void load()
  }, [load])

  // Login screen — без sidebar
  if (loading || !user) {
    return (
      <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900">
        <Outlet />
      </div>
    )
  }

  const visibleItems = NAV_ITEMS.filter(
    (it) => !it.roles || it.roles.includes(user.role),
  )

  return (
    <div className="min-h-screen flex bg-zinc-50 dark:bg-zinc-900">
      <aside className="w-60 shrink-0 border-r border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 flex flex-col">
        <div className="px-5 py-5 border-b border-zinc-200 dark:border-zinc-700">
          <Link to="/dashboard" className="flex items-center gap-2 font-bold text-zinc-900 dark:text-zinc-100">
            <span className="text-2xl">💡</span>
            <span>Фабрика идей</span>
          </Link>
          <p className="text-xs text-zinc-500 mt-1">ООО «Нордголд»</p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {visibleItems.map((item) => {
            const active =
              location.pathname === item.to ||
              (item.to !== '/dashboard' && location.pathname.startsWith(item.to))
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  active
                    ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100 font-medium'
                    : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700'
                }`}
              >
                <span className="text-base w-5 text-center">{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            )
          })}
        </nav>

        <div className="border-t border-zinc-200 dark:border-zinc-700 p-3">
          <Link
            to="/profile"
            className="block px-3 py-2 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-700"
          >
            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
              {user.full_name}
            </p>
            <p className="text-xs text-zinc-500 capitalize">{user.role}</p>
          </Link>
          <button
            onClick={() => void logout()}
            className="w-full mt-1 px-3 py-1.5 text-left text-xs text-zinc-500 hover:text-red-600"
          >
            ⎋ Выйти
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-auto">
        <Outlet />
      </main>
    </div>
  )
}
